import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@material-ui/core';
import {
  ArrowForwardRounded as ArrowIcon,
  BusinessRounded as WorkspaceIcon
} from '@material-ui/icons';
import styled from 'styled-components';

import {
  AuthContext,
  baseUrl,
  consumePendingMcpConsent,
  hasPendingMcpConsent
} from 'framework';
import { theme as appTheme } from '../theme';
import {
  createSignupPrefill,
  saveSignupPlatformToken,
  saveSignupPrefill,
  SignupPrefill
} from './signup-prefill';

interface Membership {
  orgId: string;
  orgName: string;
  orgUserId: string;
  status: 'active' | 'invited' | 'disabled';
  authProvider: 'cognito' | 'google' | 'microsoft';
}

interface PlatformAccount {
  id: string;
  email: string;
  name: string;
}

interface CheckoutLocationState {
  signupPrefill?: SignupPrefill;
}

const spacing = (...values: number[]) => {
  const themeSpacing = appTheme.spacing as (
    ...args: number[]
  ) => number | string;
  const value = themeSpacing(...values);
  return typeof value === 'number' ? `${value}px` : value;
};

const { palette } = appTheme;

const SelectionShell = styled(Box)`
  min-height: 100vh;
  padding: ${spacing(4, 2)};
  background: radial-gradient(
      circle at top left,
      rgba(246, 82, 160, 0.18),
      transparent 28%
    ),
    radial-gradient(
      circle at top right,
      rgba(48, 151, 232, 0.16),
      transparent 32%
    ),
    linear-gradient(180deg, #f5f8fd 0%, #eef3f9 100%);
`;

const SelectionCard = styled(Box)`
  max-width: 900px;
  margin: 0 auto;
  border-radius: 28px;
  overflow: hidden;
  background: ${palette.background.paper};
  box-shadow: 0 24px 60px rgba(0, 48, 96, 0.12);
`;

const SelectionHero = styled(Box)`
  padding: ${spacing(4)};
  background: linear-gradient(135deg, #003060 0%, #3097e8 100%);
  color: ${palette.primary.contrastText};
`;

const SelectionHeroTitle = styled(Typography)`
  max-width: 560px;
  font-weight: 700;
  line-height: 1.2;
`;

const SelectionHeroText = styled(Typography)`
  max-width: 620px;
  margin-top: ${spacing(1.5)};
  line-height: 1.6;
  color: rgba(247, 247, 247, 0.86);
`;

const AccountBadge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(1)};
  margin-top: ${spacing(2.5)};
  padding: ${spacing(1, 1.5)};
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const AccountBadgeText = styled(Typography)`
  color: rgba(247, 247, 247, 0.92);
`;

const SelectionBody = styled(Box)`
  padding: ${spacing(3)};
`;

const WorkspaceGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${spacing(2)};
`;

const WorkspaceTile = styled(Button)`
  && {
    display: block;
    width: 100%;
    padding: ${spacing(2.25)};
    border-radius: 20px;
    border: 1px solid rgba(0, 48, 96, 0.08);
    background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
    box-shadow: 0 10px 24px rgba(0, 48, 96, 0.08);
    text-align: left;
    text-transform: none;
    transition: transform 180ms ease, box-shadow 180ms ease,
      border-color 180ms ease;
  }

  &&:hover {
    transform: translateY(-2px);
    border-color: rgba(48, 151, 232, 0.28);
    box-shadow: 0 16px 30px rgba(0, 48, 96, 0.12);
    background: linear-gradient(180deg, #ffffff 0%, #f3f8ff 100%);
  }

  &&:disabled {
    transform: none;
    box-shadow: none;
    background: #f4f6f9;
    border-color: rgba(0, 48, 96, 0.06);
  }

  & .MuiButton-label {
    width: 100%;
  }
`;

const WorkspaceTileContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${spacing(2)};
`;

const WorkspaceAvatar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  flex: 0 0 52px;
  background: linear-gradient(135deg, #f652a0 0%, #3097e8 100%);
  color: ${palette.primary.contrastText};
  font-weight: 700;
  letter-spacing: 0.04em;
`;

const WorkspaceText = styled(Box)`
  min-width: 0;
  flex: 1 1 auto;
`;

const WorkspaceName = styled(Typography)`
  font-weight: 700;
  color: ${palette.text.primary};
`;

const WorkspaceMeta = styled(Typography)`
  margin-top: ${spacing(0.5)};
  color: ${palette.text.secondary};
`;

const WorkspaceArrow = styled(ArrowIcon)`
  color: ${palette.primary.main};
  flex: 0 0 auto;
`;

const ActionsRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing(1.5)};
  margin-top: ${spacing(2.5)};
`;

const PrimaryAction = styled(Button)`
  && {
    min-width: 200px;
    border-radius: 999px;
    padding: ${spacing(1.25, 2.75)};
    font-weight: 700;
    text-transform: none;
    box-shadow: 0 10px 24px rgba(246, 82, 160, 0.24);
  }
`;

const SecondaryAction = styled(Button)`
  && {
    min-width: 200px;
    border-radius: 999px;
    padding: ${spacing(1.25, 2.75)};
    font-weight: 700;
    text-transform: none;
  }
`;

const normalizeReturnTo = (value?: string | null): string => {
  const trimmedValue = value?.trim();

  if (
    !trimmedValue ||
    !trimmedValue.startsWith('/') ||
    trimmedValue.startsWith('//') ||
    trimmedValue.includes('://')
  ) {
    return '/checkout/small';
  }

  return trimmedValue;
};

const applyOrgToken = (orgId: string, token: string) => {
  localStorage.setItem(`spothr.authToken.${orgId}`, token);
  localStorage.removeItem(`spothr.authProvider.${orgId}`);
  localStorage.removeItem(`spothr.testToken.${orgId}`);
  // If the user was midway through connecting an MCP client, return them to the
  // consent screen with the original OAuth request rather than the dashboard.
  const pendingConsent = consumePendingMcpConsent(orgId);
  window.location.href = pendingConsent
    ? `${window.location.origin}${pendingConsent}`
    : `${window.location.origin}/${orgId}`;
};

const getWorkspaceBadge = (name?: string, fallbackId?: string) => {
  const source = (name || fallbackId || '').trim();
  const parts = source.split(/\s+/).filter(Boolean);

  if (parts.length > 1) {
    return parts
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('');
  }

  return (
    source
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase() || 'WS'
  );
};

export function Callback() {
  const { organization } = useParams<{ organization: string }>();
  const history = useHistory();
  const [authState, setAuthState] = useState<
    'uninitialized' | 'pending' | 'initialized'
  >('uninitialized');
  const [platformState, setPlatformState] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');
  const [platformErrorCode, setPlatformErrorCode] = useState<string | null>(
    null
  );
  const [platformToken, setPlatformToken] = useState<string>('');
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [
    platformAccount,
    setPlatformAccount
  ] = useState<PlatformAccount | null>(null);
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);
  const [platformIntent, setPlatformIntent] = useState<'signin' | 'signup'>(
    'signin'
  );
  const [createWorkspacePath, setCreateWorkspacePath] = useState(
    '/checkout/small'
  );
  const { t } = useTranslation();

  const { isInitialized, init, handleCallback } = useContext(AuthContext);

  const prepareWorkspaceCreationPrefill = useCallback(
    (account?: PlatformAccount | null, token?: string) => {
      const signupPrefill = account ? createSignupPrefill(account) : {};
      saveSignupPrefill(signupPrefill);
      saveSignupPlatformToken(token);
      return signupPrefill;
    },
    []
  );

  const selectMembership = useCallback(async (orgId: string, token: string) => {
    setSwitchingOrgId(orgId);
    try {
      const response = await fetch(`${baseUrl}/auth/select-org`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ orgId })
      });

      if (!response.ok) {
        throw new Error('Select org failed');
      }

      const data = (await response.json()) as { token?: string };
      if (!data.token) {
        throw new Error('Select org token missing');
      }

      applyOrgToken(orgId, data.token);
    } catch {
      setPlatformState('error');
      setPlatformErrorCode('select_org_failed');
      setSwitchingOrgId(null);
    }
  }, []);

  useEffect(() => {
    if (organization !== 'platform') {
      return;
    }

    const bootstrapPlatformCallback = async () => {
      setPlatformState('loading');
      const searchParams = new URLSearchParams(window.location.search);
      const token = searchParams.get('token') || '';
      const error = searchParams.get('error');
      const intent =
        searchParams.get('intent') === 'signup' ? 'signup' : 'signin';
      const returnTo = normalizeReturnTo(searchParams.get('returnTo'));

      setPlatformIntent(intent);
      setCreateWorkspacePath(returnTo);

      if (!token) {
        setPlatformErrorCode(error || 'missing_token');
        setPlatformState('error');
        return;
      }

      setPlatformToken(token);

      if (error && error !== 'no_memberships') {
        setPlatformErrorCode(error);
        setPlatformState('error');
        return;
      }

      try {
        const membershipsResponse = await fetch(`${baseUrl}/auth/memberships`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!membershipsResponse.ok) {
          throw new Error('Membership lookup failed');
        }

        const data = (await membershipsResponse.json()) as {
          account?: PlatformAccount;
          memberships?: Membership[];
        };
        setPlatformAccount(data.account || null);
        const activeMemberships = (data.memberships || []).filter(
          membership => membership.status === 'active'
        );

        if (activeMemberships.length === 0) {
          if (intent === 'signup') {
            history.replace(returnTo, {
              signupPrefill: prepareWorkspaceCreationPrefill(
                data.account || null,
                token
              )
            } as CheckoutLocationState);
            return;
          }

          setPlatformErrorCode('no_memberships');
          setPlatformState('error');
          return;
        }

        // Normally a single workspace is auto-selected. During an MCP consent,
        // keep the picker visible so the user explicitly chooses the workspace
        // to connect (and so the selection runs the consent-resume path).
        if (
          activeMemberships.length === 1 &&
          intent !== 'signup' &&
          !hasPendingMcpConsent()
        ) {
          await selectMembership(activeMemberships[0].orgId, token);
          return;
        }

        setMemberships(activeMemberships);
        setPlatformState('ready');
      } catch {
        setPlatformErrorCode('membership_lookup_failed');
        setPlatformState('error');
      }
    };

    if (platformState === 'idle') {
      bootstrapPlatformCallback();
    }
  }, [
    history,
    organization,
    platformState,
    prepareWorkspaceCreationPrefill,
    selectMembership
  ]);

  useEffect(() => {
    if (organization === 'platform') {
      return;
    }

    if (authState !== 'initialized') {
      return;
    }
    handleCallback();
  }, [handleCallback, authState, organization]);

  if (organization === 'platform') {
    if (platformState === 'loading' || platformState === 'idle') {
      return <>{t('loading')}</>;
    }

    const isSignupIntent = platformIntent === 'signup';

    if (platformState === 'error') {
      return (
        <SelectionShell>
          <SelectionCard>
            <SelectionHero>
              <SelectionHeroTitle variant="h4">
                {platformErrorCode === 'no_memberships'
                  ? t('noWorkspaceMembershipTitle')
                  : t('workspaceSelectionFailed')}
              </SelectionHeroTitle>
              <SelectionHeroText variant="body1">
                {platformErrorCode === 'no_memberships'
                  ? t('noWorkspaceMembershipDescription')
                  : t('workspaceSelectionFailedDescription')}
              </SelectionHeroText>
              {platformAccount?.email ? (
                <AccountBadge>
                  <WorkspaceIcon fontSize="small" />
                  <AccountBadgeText variant="body2">
                    {platformAccount.email}
                  </AccountBadgeText>
                </AccountBadge>
              ) : null}
            </SelectionHero>
            <SelectionBody>
              <ActionsRow>
                {platformErrorCode === 'no_memberships' ? (
                  <PrimaryAction
                    color="secondary"
                    variant="contained"
                    onClick={() => history.push(createWorkspacePath)}
                  >
                    {t('createWorkspace')}
                  </PrimaryAction>
                ) : null}
                <SecondaryAction
                  variant="outlined"
                  onClick={() => history.push('/signin')}
                >
                  {t('backToSignin')}
                </SecondaryAction>
              </ActionsRow>
            </SelectionBody>
          </SelectionCard>
        </SelectionShell>
      );
    }

    return (
      <SelectionShell>
        <SelectionCard>
          <SelectionHero>
            <SelectionHeroTitle variant="h4">
              {isSignupIntent
                ? t('existingWorkspaceMembershipTitle')
                : t('chooseWorkspace')}
            </SelectionHeroTitle>
            <SelectionHeroText variant="body1">
              {isSignupIntent
                ? t('existingWorkspaceMembershipDescription')
                : t('chooseWorkspaceHint')}
            </SelectionHeroText>
            {platformAccount?.email ? (
              <AccountBadge>
                <WorkspaceIcon fontSize="small" />
                <AccountBadgeText variant="body2">
                  {platformAccount.email}
                </AccountBadgeText>
              </AccountBadge>
            ) : null}
          </SelectionHero>
          <SelectionBody>
            <WorkspaceGrid>
              {memberships.map(membership => (
                <WorkspaceTile
                  key={membership.orgId}
                  disabled={switchingOrgId === membership.orgId}
                  onClick={() =>
                    selectMembership(membership.orgId, platformToken)
                  }
                >
                  <WorkspaceTileContent>
                    <WorkspaceAvatar>
                      {getWorkspaceBadge(membership.orgName, membership.orgId)}
                    </WorkspaceAvatar>
                    <WorkspaceText>
                      <WorkspaceName variant="body1">
                        {switchingOrgId === membership.orgId
                          ? t('switchingWorkspace')
                          : membership.orgName || membership.orgId}
                      </WorkspaceName>
                      <WorkspaceMeta variant="body2">
                        {membership.orgId}
                      </WorkspaceMeta>
                    </WorkspaceText>
                    <WorkspaceArrow />
                  </WorkspaceTileContent>
                </WorkspaceTile>
              ))}
            </WorkspaceGrid>
            {isSignupIntent ? (
              <ActionsRow>
                <SecondaryAction
                  variant="outlined"
                  onClick={() => {
                    history.push(createWorkspacePath, {
                      signupPrefill: prepareWorkspaceCreationPrefill(
                        platformAccount,
                        platformToken
                      )
                    } as CheckoutLocationState);
                  }}
                >
                  {t('createWorkspace')}
                </SecondaryAction>
              </ActionsRow>
            ) : null}
          </SelectionBody>
        </SelectionCard>
      </SelectionShell>
    );
  }

  if (!isInitialized()) {
    const initAuth = async () => {
      setAuthState('pending');
      await init(organization);
      setAuthState('initialized');
    };

    if (authState === 'uninitialized') {
      initAuth();
    }

    return <>{t('loading')}</>;
  }

  return <>{t('loading')}</>;
}
